import { lazy, type ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { CustomerShell, OwnerShell, PublicShell, SupportShell } from './components/shell/index.js';
import { RequireAuth, RequireCapability } from './lib/auth/guards.js';
import { TeraAgoLoginPage } from './cityload/pages/login.js';
import { TeraAgoEntry } from './cityload/pages/entry.js';
import { LandingPage } from './pages/public/LandingPage.js';
import { EquipmentSearchPage } from './pages/public/EquipmentSearchPage.js';
import { EquipmentDetailPage } from './pages/public/EquipmentDetailPage.js';
import { BlockedPage } from './pages/placeholders.js';

/*
  TeraAgo's admin console. Lazy like every other guarded surface, and for a
  stronger reason: it is a different PRODUCT sharing this bundle, and a
  customer on the landing page should never download a dispatcher's console.

  It is deliberately un-guarded for now. This is a UI pass — the screens exist
  to be looked at and compared against the approved mockups, and an auth wall
  in front of them would only be a wall in front of the review. The guard goes
  on with the API, in the same change that gives it something to protect.
*/
const TeraAgoDashboard = lazy(() =>
  import('./cityload/DashboardPage.js').then((m) => ({ default: m.DashboardPage })),
);
/*
  The console's sections arrive in three chunks rather than seventeen. A
  dispatcher who opens Bookings will open Drivers next, and splitting every
  page individually would trade one download for six.
*/
const TeraAgoOps = {
  Bookings: lazy(() =>
    import('./cityload/pages/operations.js').then((m) => ({ default: m.BookingsPage })),
  ),
  Drivers: lazy(() =>
    import('./cityload/pages/operations.js').then((m) => ({ default: m.DriversPage })),
  ),
  Customers: lazy(() =>
    import('./cityload/pages/operations.js').then((m) => ({ default: m.CustomersPage })),
  ),
  Kyc: lazy(() => import('./cityload/pages/operations.js').then((m) => ({ default: m.KycPage }))),
  BookingDetail: lazy(() =>
    import('./cityload/pages/operations.js').then((m) => ({ default: m.BookingDetailPage })),
  ),
};
/*
  The queue and the one driver it opens onto are one chunk. Nobody reviews a
  price without opening a driver, and splitting them would trade one download
  for two on the first click.
*/
const TeraAgoApprovals = {
  Queue: lazy(() =>
    import('./cityload/pages/approvals.js').then((m) => ({ default: m.RateApprovalsPage })),
  ),
  Driver: lazy(() =>
    import('./cityload/pages/approvals.js').then((m) => ({ default: m.RateApprovalPage })),
  ),
};
const TeraAgoMoney = {
  Vehicles: lazy(() =>
    import('./cityload/pages/money.js').then((m) => ({ default: m.VehiclesPage })),
  ),
  Pricing: lazy(() =>
    import('./cityload/pages/pricing-control.js').then((m) => ({ default: m.PriceControlPage })),
  ),
  Payments: lazy(() =>
    import('./cityload/pages/money.js').then((m) => ({ default: m.PaymentsPage })),
  ),
  Wallet: lazy(() => import('./cityload/pages/money.js').then((m) => ({ default: m.WalletPage }))),
};
/*
  Heavy equipment: one chunk for its four screens. A dispatcher watching a
  machine job opens its operator and its price within the same minute.
*/
const TeraAgoEquipment = {
  Jobs: lazy(() =>
    import('./cityload/pages/equipment.js').then((m) => ({ default: m.MachineJobsPage })),
  ),
  Operators: lazy(() =>
    import('./cityload/pages/equipment.js').then((m) => ({ default: m.OperatorsPage })),
  ),
  MoneyRules: lazy(() =>
    import('./cityload/pages/money-rules.js').then((m) => ({ default: m.MoneyRulesPage })),
  ),
  Breakdowns: lazy(() =>
    import('./cityload/pages/money-rules.js').then((m) => ({ default: m.BreakdownsPage })),
  ),
  Machines: lazy(() =>
    import('./cityload/pages/equipment.js').then((m) => ({ default: m.MachinesPage })),
  ),
  /* Its own chunk: a dispatcher clearing renter papers is not on their way to
     a machine job, and the review screen carries an image loader nothing else
     needs. */
  RentalPapers: lazy(() =>
    import('./cityload/pages/rental-papers.js').then((m) => ({ default: m.RentalPapersPage })),
  ),
  RentalDisputes: lazy(() =>
    import('./cityload/pages/rental-disputes.js').then((m) => ({ default: m.RentalDisputesPage })),
  ),
  Hire: lazy(() => import('./cityload/pages/hire.js').then((m) => ({ default: m.HirePage }))),
  Bids: lazy(() => import('./cityload/pages/bids.js').then((m) => ({ default: m.BidsPage }))),
  Analytics: lazy(() =>
    import('./cityload/pages/analytics.js').then((m) => ({ default: m.AnalyticsPage })),
  ),
  Contracts: lazy(() =>
    import('./cityload/pages/contracts.js').then((m) => ({ default: m.ContractsPage })),
  ),
  Subscriptions: lazy(() =>
    import('./cityload/pages/subscriptions.js').then((m) => ({ default: m.SubscriptionsPage })),
  ),
  Sos: lazy(() => import('./cityload/pages/sos.js').then((m) => ({ default: m.SosPage }))),
  Catalogue: lazy(() =>
    import('./cityload/pages/catalogue.js').then((m) => ({ default: m.CataloguePage })),
  ),
  Services: lazy(() =>
    import('./cityload/pages/services.js').then((m) => ({ default: m.ServicesPage })),
  ),
  Charges: lazy(() =>
    import('./cityload/pages/charges.js').then((m) => ({ default: m.ChargesPage })),
  ),
  Pricing: lazy(() =>
    import('./cityload/pages/equipment.js').then((m) => ({ default: m.MachinePricingPage })),
  ),
};
/*
  Companies, drivers and machines (M069): the admin's fleet screens in one
  chunk, the company's console in another. A dispatcher checking a company
  opens its machines next; a company never downloads the admin's screens.
*/
const TeraAgoFleet = {
  Overview: lazy(() =>
    import('./cityload/pages/fleet.js').then((m) => ({ default: m.FleetOverviewPage })),
  ),
  Companies: lazy(() =>
    import('./cityload/pages/fleet.js').then((m) => ({ default: m.CompaniesPage })),
  ),
  Company: lazy(() =>
    import('./cityload/pages/fleet.js').then((m) => ({ default: m.CompanyDetailPage })),
  ),
  Machines: lazy(() =>
    import('./cityload/pages/fleet.js').then((m) => ({ default: m.FleetMachinesPage })),
  ),
  Machine: lazy(() =>
    import('./cityload/pages/fleet.js').then((m) => ({ default: m.FleetMachinePage })),
  ),
};
const TeraAgoCompany = {
  Overview: lazy(() =>
    import('./cityload/company/pages.js').then((m) => ({ default: m.CompanyOverviewPage })),
  ),
  Drivers: lazy(() =>
    import('./cityload/company/pages.js').then((m) => ({ default: m.CompanyDriversPage })),
  ),
  Machines: lazy(() =>
    import('./cityload/company/pages.js').then((m) => ({ default: m.CompanyMachinesPage })),
  ),
  Profile: lazy(() =>
    import('./cityload/company/pages.js').then((m) => ({ default: m.CompanyProfilePage })),
  ),
  Join: lazy(() => import('./cityload/company/join.js').then((m) => ({ default: m.JoinPage }))),
  Rentals: lazy(() =>
    import('./cityload/company/rentals.js').then((m) => ({ default: m.CompanyRentalsPage })),
  ),
  Loads: lazy(() =>
    import('./cityload/company/loads.js').then((m) => ({ default: m.CompanyLoadsPage })),
  ),
  Money: lazy(() =>
    import('./cityload/company/rentals.js').then((m) => ({ default: m.CompanyMachineMoneyPage })),
  ),
};
const TeraAgoPlatform = {
  Tracking: lazy(() =>
    import('./cityload/pages/platform.js').then((m) => ({ default: m.TrackingPage })),
  ),
  Complaints: lazy(() =>
    import('./cityload/pages/platform.js').then((m) => ({ default: m.ComplaintsPage })),
  ),
  Promos: lazy(() =>
    import('./cityload/pages/platform.js').then((m) => ({ default: m.PromosPage })),
  ),
  Reports: lazy(() =>
    import('./cityload/pages/platform.js').then((m) => ({ default: m.ReportsPage })),
  ),
  Areas: lazy(() => import('./cityload/pages/platform.js').then((m) => ({ default: m.AreasPage }))),
  Notifications: lazy(() =>
    import('./cityload/pages/platform.js').then((m) => ({ default: m.NotificationsPage })),
  ),
  Settings: lazy(() =>
    import('./cityload/pages/platform.js').then((m) => ({ default: m.SettingsPage })),
  ),
  AdminUsers: lazy(() =>
    import('./cityload/pages/platform.js').then((m) => ({ default: m.AdminUsersPage })),
  ),
};
/*
  The DRIVER and vehicle-owner console.

  Its own chunk, and a separate one from the dispatcher's: the two consoles
  share a frame and a palette but never a reader, and a driver on a yard
  connection should not download seventeen admin sections to reach their wallet.
  One chunk rather than seven, because a driver who opens Today opens the offer
  and the trip within the next minute — splitting those would trade one download
  for three at the busiest moment of a shift.
*/
const TeraAgoPartner = {
  Today: lazy(() => import('./cityload/partner/today.js').then((m) => ({ default: m.TodayPage }))),
  Offer: lazy(() => import('./cityload/partner/today.js').then((m) => ({ default: m.OfferPage }))),
  Trip: lazy(() => import('./cityload/partner/today.js').then((m) => ({ default: m.TripPage }))),
  Price: lazy(() =>
    import('./cityload/partner/pricing.js').then((m) => ({ default: m.MyPricePage })),
  ),
  Earnings: lazy(() =>
    import('./cityload/partner/money.js').then((m) => ({ default: m.EarningsPage })),
  ),
  Documents: lazy(() =>
    import('./cityload/partner/profile.js').then((m) => ({ default: m.DocumentsPage })),
  ),
  Vehicles: lazy(() =>
    import('./cityload/partner/profile.js').then((m) => ({ default: m.VehiclesPage })),
  ),
  Bids: lazy(() =>
    import('./cityload/partner/bids.js').then((m) => ({ default: m.PartnerBidsPage })),
  ),
};
/**
 * Everything behind a guard is its own chunk.
 *
 * A visitor on the landing page has no use for the support console, the rate-
 * card editor or the billing stack, and should not download them. The public
 * routes stay eagerly imported because they ARE the entry point and must paint
 * without a second round-trip.
 */
const OwnerHomePage = lazy(() =>
  import('./pages/owner/OwnerHomePage.js').then((m) => ({ default: m.OwnerHomePage })),
);
const FleetPage = lazy(() =>
  import('./pages/owner/FleetPage.js').then((m) => ({ default: m.FleetPage })),
);
const RequestToBookPage = lazy(() =>
  import('./pages/offers/RequestToBookPage.js').then((m) => ({ default: m.RequestToBookPage })),
);
const OwnerEquipmentLayout = lazy(() =>
  import('./pages/owner/OwnerEquipmentLayout.js').then((m) => ({
    default: m.OwnerEquipmentLayout,
  })),
);
const OwnerEquipmentDetail = lazy(() =>
  import('./pages/owner/OwnerEquipmentDetail.js').then((m) => ({
    default: m.OwnerEquipmentDetail,
  })),
);
const PhotosPage = lazy(() =>
  import('./pages/owner/PhotosPage.js').then((m) => ({ default: m.PhotosPage })),
);
const DocumentsPage = lazy(() =>
  import('./pages/owner/DocumentsPage.js').then((m) => ({ default: m.DocumentsPage })),
);
const AttachmentsPage = lazy(() =>
  import('./pages/owner/AttachmentsPage.js').then((m) => ({ default: m.AttachmentsPage })),
);
const RateCardPage = lazy(() =>
  import('./pages/owner/RateCardPage.js').then((m) => ({ default: m.RateCardPage })),
);
const OwnerAvailabilityPage = lazy(() =>
  import('./pages/owner/OwnerAvailabilityPage.js').then((m) => ({
    default: m.OwnerAvailabilityPage,
  })),
);
const OfferListPage = lazy(() =>
  import('./pages/offers/OfferListPage.js').then((m) => ({ default: m.OfferListPage })),
);
const OfferDetailPage = lazy(() =>
  import('./pages/offers/OfferDetailPage.js').then((m) => ({ default: m.OfferDetailPage })),
);
const BookingListPage = lazy(() =>
  import('./pages/bookings/BookingListPage.js').then((m) => ({ default: m.BookingListPage })),
);
const BookingDetailPage = lazy(() =>
  import('./pages/bookings/BookingDetailPage.js').then((m) => ({ default: m.BookingDetailPage })),
);
const PaymentPage = lazy(() =>
  import('./pages/bookings/PaymentPage.js').then((m) => ({ default: m.PaymentPage })),
);
const TransportPage = lazy(() =>
  import('./pages/operations/TransportPage.js').then((m) => ({ default: m.TransportPage })),
);
const HandoverPage = lazy(() =>
  import('./pages/operations/HandoverPage.js').then((m) => ({ default: m.HandoverPage })),
);
const MeterReadingsPage = lazy(() =>
  import('./pages/operations/MeterReadingsPage.js').then((m) => ({ default: m.MeterReadingsPage })),
);
const ExtensionsPage = lazy(() =>
  import('./pages/operations/ExtensionsPage.js').then((m) => ({ default: m.ExtensionsPage })),
);
const ReturnPage = lazy(() =>
  import('./pages/billing/ReturnPage.js').then((m) => ({ default: m.ReturnPage })),
);
const BillPage = lazy(() =>
  import('./pages/billing/BillPage.js').then((m) => ({ default: m.BillPage })),
);
const CancelPage = lazy(() =>
  import('./pages/billing/CancelPage.js').then((m) => ({ default: m.CancelPage })),
);
const AccountPage = lazy(() =>
  import('./pages/AccountPage.js').then((m) => ({ default: m.AccountPage })),
);
const EvidenceDetailPage = lazy(() =>
  import('./pages/operations/EvidenceDetailPage.js').then((m) => ({
    default: m.EvidenceDetailPage,
  })),
);
const SupportHomePage = lazy(() =>
  import('./pages/support/SupportHomePage.js').then((m) => ({ default: m.SupportHomePage })),
);
const SupportBookingPage = lazy(() =>
  import('./pages/support/SupportBookingPage.js').then((m) => ({ default: m.SupportBookingPage })),
);
const IdentityCorrectionPage = lazy(() =>
  import('./pages/support/IdentityCorrectionPage.js').then((m) => ({
    default: m.IdentityCorrectionPage,
  })),
);
const EvidencePage = lazy(() =>
  import('./pages/operations/EvidencePage.js').then((m) => ({ default: m.EvidencePage })),
);
const BookingTimelinePage = lazy(() =>
  import('./pages/operations/EvidencePage.js').then((m) => ({ default: m.BookingTimelinePage })),
);

/**
 * Route tree — FRONTEND_ARCHITECTURE.md §2.
 *
 * Public routes are open. Everything under /app, /owner and /support is behind
 * RequireAuth, then a capability guard. A user who lacks the capability sees
 * the SAME "not available" state a 404 produces (§4.4).
 */
export function AppRoutes(): ReactElement {
  return (
    <Routes>
      {/*
        ---- TeraAgo admin console ------------------------------------

        Behind the SAME guard as every other console in this app: a session,
        then a capability. `cl_admin` is a TeraAgo capability on an ordinary
        `party.account`, so a dispatcher signs in through the same OTP flow as
        everyone else and an Equipment Rental customer reaching /admin is
        refused by capability rather than by URL.

        The API refuses independently — these routes are 401/403 without a
        token regardless of what the browser renders. This guard exists so the
        user meets a sentence instead of six empty tables.
      */}
      <Route element={<RequireAuth />}>
        <Route element={<RequireCapability any={['cl_admin']} />}>
          <Route path="/admin" element={<TeraAgoDashboard />} />
          <Route path="/admin/sos" element={<TeraAgoEquipment.Sos />} />
          <Route path="/admin/analytics" element={<TeraAgoEquipment.Analytics />} />
          <Route path="/admin/tracking" element={<TeraAgoPlatform.Tracking />} />
          <Route path="/admin/bookings" element={<TeraAgoOps.Bookings />} />
          {/*
            ONE BOOKING. `GET /v1/cityload/bookings/:id` has existed the whole
            time with no caller, and the table had no row action — so an
            operator answering "where is CLD12548" had one line of a fifty-row
            table and no way to open it.
          */}
          <Route path="/admin/bookings/:bookingId" element={<TeraAgoOps.BookingDetail />} />
          <Route path="/admin/customers" element={<TeraAgoOps.Customers />} />
          <Route path="/admin/drivers" element={<TeraAgoOps.Drivers />} />
          <Route path="/admin/kyc" element={<TeraAgoOps.Kyc />} />
          <Route path="/admin/rate-approvals" element={<TeraAgoApprovals.Queue />} />
          <Route path="/admin/rate-approvals/:driverId" element={<TeraAgoApprovals.Driver />} />
          <Route path="/admin/vehicles" element={<TeraAgoMoney.Vehicles />} />
          <Route path="/admin/rental-papers" element={<TeraAgoEquipment.RentalPapers />} />
          <Route path="/admin/rental-disputes" element={<TeraAgoEquipment.RentalDisputes />} />
          <Route path="/admin/bids" element={<TeraAgoEquipment.Bids />} />
          <Route path="/admin/hire" element={<TeraAgoEquipment.Hire />} />
          <Route path="/admin/contracts" element={<TeraAgoEquipment.Contracts />} />
          <Route path="/admin/pricing" element={<TeraAgoMoney.Pricing />} />
          {/* Heavy equipment (CITYLOAD_HEAVY_EQUIPMENT.md section 8). */}
          <Route
            path="/admin/equipment"
            element={<Navigate to="/admin/equipment/jobs" replace />}
          />
          <Route path="/admin/equipment/jobs" element={<TeraAgoEquipment.Jobs />} />
          <Route path="/admin/equipment/operators" element={<TeraAgoEquipment.Operators />} />
          <Route path="/admin/equipment/machines" element={<TeraAgoEquipment.Machines />} />
          <Route path="/admin/money-rules" element={<TeraAgoEquipment.MoneyRules />} />
          <Route path="/admin/subscriptions" element={<TeraAgoEquipment.Subscriptions />} />
          <Route path="/admin/breakdowns" element={<TeraAgoEquipment.Breakdowns />} />
          <Route path="/admin/equipment/pricing" element={<TeraAgoEquipment.Pricing />} />
          {/* Companies, drivers and machines (M069). */}
          <Route path="/admin/fleet" element={<TeraAgoFleet.Overview />} />
          <Route path="/admin/fleet/machines" element={<TeraAgoFleet.Machines />} />
          <Route path="/admin/catalogue" element={<TeraAgoEquipment.Catalogue />} />
          <Route path="/admin/services" element={<TeraAgoEquipment.Services />} />
          <Route path="/admin/charges" element={<TeraAgoEquipment.Charges />} />
          <Route path="/admin/fleet/machines/:machineId" element={<TeraAgoFleet.Machine />} />
          <Route path="/admin/companies" element={<TeraAgoFleet.Companies />} />
          <Route path="/admin/companies/:companyId" element={<TeraAgoFleet.Company />} />
          <Route path="/admin/payments" element={<TeraAgoMoney.Payments />} />
          <Route path="/admin/wallet" element={<TeraAgoMoney.Wallet />} />
          <Route path="/admin/complaints" element={<TeraAgoPlatform.Complaints />} />
          <Route path="/admin/promos" element={<TeraAgoPlatform.Promos />} />
          <Route path="/admin/reports" element={<TeraAgoPlatform.Reports />} />
          <Route path="/admin/areas" element={<TeraAgoPlatform.Areas />} />
          <Route path="/admin/notifications" element={<TeraAgoPlatform.Notifications />} />
          <Route path="/admin/settings" element={<TeraAgoPlatform.Settings />} />
          <Route path="/admin/users" element={<TeraAgoPlatform.AdminUsers />} />
        </Route>
      </Route>

      {/*
        ---- TeraAgo driver console -----------------------------------

        The SAME two guards as the admin console next door, and a different
        capability — which is the whole of what keeps a driver out of `/admin`
        and an admin out of `/partner`. Neither is kept out by URL: §4.1 makes
        the capability the boundary, so an account that holds both simply has
        both consoles.

        The API refuses independently. Every route under here is 401 or 404
        without a driver's own session, because the endpoints are scoped by the
        signing-in account rather than by an id the browser sends. This guard
        exists so a person meets a sentence instead of seven failing screens.
      */}
      <Route element={<RequireAuth />}>
        <Route element={<RequireCapability any={['cl_driver']} />}>
          <Route path="/partner" element={<TeraAgoPartner.Today />} />
          <Route path="/partner/offer" element={<TeraAgoPartner.Offer />} />
          <Route path="/partner/trip" element={<TeraAgoPartner.Trip />} />
          <Route path="/partner/price" element={<TeraAgoPartner.Price />} />
          <Route path="/partner/earnings" element={<TeraAgoPartner.Earnings />} />
          <Route path="/partner/documents" element={<TeraAgoPartner.Documents />} />
          <Route path="/partner/vehicles" element={<TeraAgoPartner.Vehicles />} />
          <Route path="/partner/bids" element={<TeraAgoPartner.Bids />} />
        </Route>
      </Route>

      {/*
        ---- TeraAgo company console (M069) ----------------------------

        The same two guards and a third capability. A company sees only its
        own drivers and machines because the API finds the company by the
        signed-in account; this guard only saves a person from four empty
        screens.
      */}
      <Route element={<RequireAuth />}>
        <Route element={<RequireCapability any={['cl_company']} />}>
          <Route path="/company" element={<TeraAgoCompany.Overview />} />
          <Route path="/company/drivers" element={<TeraAgoCompany.Drivers />} />
          <Route path="/company/machines" element={<TeraAgoCompany.Machines />} />
          <Route path="/company/loads" element={<TeraAgoCompany.Loads />} />
          <Route path="/company/rentals" element={<TeraAgoCompany.Rentals />} />
          <Route path="/company/money" element={<TeraAgoCompany.Money />} />
          <Route path="/company/profile" element={<TeraAgoCompany.Profile />} />
        </Route>
        {/* Registering as a company or a driver: any signed-in account. */}
        <Route path="/join" element={<TeraAgoCompany.Join />} />
      </Route>

      {/*
        ---- sign in ----------------------------------------------------

        OUTSIDE `PublicShell`, and that is the point of it being here rather
        than three lines down with the other open routes. The shell is the
        equipment-hire product's chrome — its brand lockup and a topbar
        offering to browse machines or list your own — and it was wrapping the
        console's own front door, so an admin arriving to dispatch a dumper met
        a different product's landing furniture. Sign-in carries TeraAgo's
        frame itself and borrows nobody's.

        Eager, like the other routes a visitor can reach without a session:
        every guard in this file redirects here, and a chunk fetched on the way
        to a login form is a second round-trip in front of the one screen that
        cannot afford one.
      */}
      <Route path="login" element={<TeraAgoLoginPage />} />

      {/*
        The root belongs to whoever arrives, not to a fixed console.

        Two consoles share this build. `vercel.json` used to redirect `/` to
        `/admin`, which sent every DRIVER into a console their capability
        forbids — they met a guard instead of their own work. The person decides
        now, so the same build can also serve a driver-facing hostname.
      */}
      <Route index element={<TeraAgoEntry />} />

      {/* ---- public --------------------------------------------------- */}
      <Route element={<PublicShell />}>
        <Route path="home" element={<LandingPage />} />
        <Route path="equipment" element={<EquipmentSearchPage />} />
        <Route path="equipment/:id" element={<EquipmentDetailPage />} />
      </Route>

      {/* ---- customer -------------------------------------------------- */}
      <Route element={<RequireAuth />}>
        <Route element={<RequireCapability any={['customer']} />}>
          <Route path="/app" element={<CustomerShell />}>
            <Route index element={<BookingListPage />} />
            <Route path="bookings" element={<BookingListPage />} />
            <Route path="bookings/:id" element={<BookingDetailPage seat="customer" />} />
            <Route path="bookings/:id/payment" element={<PaymentPage />} />
            <Route path="bookings/:id/evidence" element={<EvidencePage />} />
            <Route path="bookings/:id/timeline" element={<BookingTimelinePage />} />
            <Route path="bookings/:id/bill" element={<BillPage seat="customer" />} />
            <Route path="bookings/:id/return" element={<ReturnPage seat="customer" />} />
            <Route path="bookings/:id/cancel" element={<CancelPage seat="customer" />} />
            <Route path="bookings/:id/transport" element={<TransportPage seat="customer" />} />
            <Route path="bookings/:id/handover" element={<HandoverPage />} />
            <Route
              path="bookings/:id/meter-readings"
              element={<MeterReadingsPage seat="customer" />}
            />
            <Route path="bookings/:id/extensions" element={<ExtensionsPage seat="customer" />} />
            <Route path="offers" element={<OfferListPage seat="customer" />} />
            <Route path="offers/:id" element={<OfferDetailPage seat="customer" />} />
            <Route path="equipment/:id/request" element={<RequestToBookPage />} />
            <Route path="account" element={<AccountPage />} />
            <Route
              path="bookings/:id/evidence/:eid"
              element={<EvidenceDetailPage backTo={(b) => `/app/bookings/${b}/evidence`} />}
            />
          </Route>
        </Route>

        {/* ---- owner --------------------------------------------------- */}
        <Route element={<RequireCapability any={['owner']} />}>
          <Route path="/owner" element={<OwnerShell />}>
            <Route index element={<OwnerHomePage />} />
            <Route path="equipment" element={<FleetPage />} />
            <Route path="equipment/:id" element={<OwnerEquipmentLayout />}>
              <Route index element={<OwnerEquipmentDetail />} />
              <Route path="photos" element={<PhotosPage />} />
              <Route path="documents" element={<DocumentsPage />} />
              <Route path="attachments" element={<AttachmentsPage />} />
              <Route path="rate-card" element={<RateCardPage />} />
              <Route path="availability" element={<OwnerAvailabilityPage />} />
            </Route>
            <Route path="offers" element={<OfferListPage seat="owner" />} />
            <Route path="offers/:id" element={<OfferDetailPage seat="owner" />} />
            <Route path="bookings/:id" element={<BookingDetailPage seat="owner" />} />
            <Route path="bookings/:id/transport" element={<TransportPage seat="owner" />} />
            <Route
              path="bookings/:id/meter-readings"
              element={<MeterReadingsPage seat="owner" />}
            />
            <Route path="bookings/:id/extensions" element={<ExtensionsPage seat="owner" />} />
            <Route path="bookings/:id/evidence" element={<EvidencePage />} />
            <Route path="bookings/:id/timeline" element={<BookingTimelinePage />} />
            {/* OD-6 + finance.payout: the single largest owner-facing gap. */}
            <Route
              path="payouts"
              element={
                <BlockedPage
                  title="Payouts"
                  capability="Payouts"
                  reason="Payouts open once the platform's tax treatment is confirmed. Your rentals and bills are unaffected, and nothing is being withheld from you in the meantime."
                />
              }
            />
          </Route>
        </Route>

        {/* ---- support -------------------------------------------------- */}
        <Route element={<RequireCapability any={['admin', 'support']} />}>
          <Route path="/support" element={<SupportShell />}>
            <Route index element={<SupportHomePage />} />
            <Route path="bookings/:id" element={<SupportBookingPage />} />
            <Route
              path="bookings/:id/evidence/:eid"
              element={<EvidenceDetailPage backTo={(b) => `/support/bookings/${b}`} />}
            />
            <Route path="equipment/:id/identity" element={<IdentityCorrectionPage />} />
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
